// frontend/src/pages/PricingPage.jsx

import { useEffect, useState } from 'react'
import { Layout } from '../components/Layout/Layout'
import { Card } from '../components/Common/Card'
import { paymentsAPI } from '../services/api'
import {
  CreditCard, CheckCircle, Clock, XCircle, Crown, Zap,
  Calendar, Loader
} from 'lucide-react'
import toast from 'react-hot-toast'

const STATUS_LABELS = {
  pending: { label: 'Ожидает оплаты', color: 'bg-yellow-100 text-yellow-700', icon: Clock },
  waiting_for_capture: { label: 'Обрабатывается', color: 'bg-blue-100 text-blue-700', icon: Clock },
  succeeded: { label: 'Оплачен', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  canceled: { label: 'Отменён', color: 'bg-red-100 text-red-700', icon: XCircle },
}

const PLAN_ICONS = {
  month_1: Zap,
  month_3: Calendar,
  year_1: Crown,
}

export const PricingPage = () => {
  const [plans, setPlans] = useState([])
  const [subscription, setSubscription] = useState(null)
  const [payments, setPayments] = useState([])
  const [loading, setLoading] = useState(true)
  const [payingPlanId, setPayingPlanId] = useState(null)

  const loadData = async () => {
    try {
      const [plansRes, subRes, paymentsRes] = await Promise.all([
        paymentsAPI.getPlans(),
        paymentsAPI.getMySubscription(),
        paymentsAPI.getMyPayments(),
      ])
      setPlans(plansRes.data.plans || [])
      setSubscription(subRes.data.subscription)
      setPayments(paymentsRes.data.payments || [])
    } catch (error) {
      console.error('Load pricing data error:', error)
      toast.error('Не удалось загрузить данные о тарифах')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadData()

    // После возврата со страницы оплаты ЮKassa - проверяем незавершённые платежи
    const checkPendingPayments = async () => {
      try {
        const res = await paymentsAPI.getMyPayments()
        const pending = (res.data.payments || []).filter(
          p => p.status === 'pending' || p.status === 'waiting_for_capture'
        )
        for (const p of pending) {
          const statusRes = await paymentsAPI.checkPaymentStatus(p.id)
          if (statusRes.data.payment?.status === 'succeeded') {
            toast.success(`Оплата тарифа «${p.plan_name}» прошла успешно!`)
            loadData()
          }
        }
      } catch (e) {
        // Тихо игнорируем - это фоновая проверка
        console.warn('Pending payments check:', e)
      }
    }
    checkPendingPayments()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handlePay = async (planId) => {
    setPayingPlanId(planId)
    try {
      const response = await paymentsAPI.createPayment(planId)
      const payment = response.data.payment

      if (payment?.confirmation_url) {
        // Перенаправляем на страницу оплаты ЮKassa
        window.location.href = payment.confirmation_url
      } else {
        toast.error('Не удалось получить ссылку на оплату')
        setPayingPlanId(null)
      }
    } catch (error) {
      console.error('Create payment error:', error)
      toast.error(error.response?.data?.detail || 'Ошибка при создании платежа')
      setPayingPlanId(null)
    }
  }

  if (loading) {
    return (
      <Layout>
        <div className="flex justify-center items-center py-20">
          <Loader className="animate-spin text-blue-600" size={40} />
        </div>
      </Layout>
    )
  }

  return (
    <Layout>
      <div className="space-y-8">
        {/* Заголовок */}
        <div className="text-center">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Тарифы и оплата</h1>
          <p className="text-gray-600">
            Выберите подходящий тариф. Оплата через ЮKassa (Яндекс) — карты, СБП, ЮMoney.
          </p>
        </div>

        {/* Текущая подписка */}
        {subscription && (
          <Card>
            <div className="flex items-center gap-4">
              <div className="flex items-center justify-center w-12 h-12 rounded-full bg-green-100">
                <CheckCircle className="text-green-600" size={24} />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900">
                  Активная подписка: {subscription.plan_name}
                </h3>
                <p className="text-sm text-gray-600">
                  Действует до{' '}
                  <span className="font-medium">
                    {new Date(subscription.expires_at).toLocaleDateString('ru-RU', {
                      day: 'numeric', month: 'long', year: 'numeric'
                    })}
                  </span>
                </p>
              </div>
              <span className="px-3 py-1 bg-green-100 text-green-700 text-sm font-medium rounded-full">
                Активна
              </span>
            </div>
          </Card>
        )}

        {/* Тарифы */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {plans.map((plan) => {
            const Icon = PLAN_ICONS[plan.id] || Zap
            const isPopular = plan.id === 'month_3'
            const isPaying = payingPlanId === plan.id

            return (
              <div
                key={plan.id}
                className={`relative bg-white rounded-xl shadow-md border-2 p-6 flex flex-col transition-transform hover:scale-105 ${
                  isPopular ? 'border-blue-500' : 'border-transparent'
                }`}
              >
                {isPopular && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-blue-600 text-white text-xs font-bold rounded-full">
                    Популярный
                  </span>
                )}

                <div className="flex items-center gap-3 mb-4">
                  <div className={`flex items-center justify-center w-10 h-10 rounded-lg ${
                    isPopular ? 'bg-blue-100' : 'bg-gray-100'
                  }`}>
                    <Icon size={20} className={isPopular ? 'text-blue-600' : 'text-gray-600'} />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">{plan.name}</h3>
                </div>

                <div className="mb-4">
                  <span className="text-3xl font-bold text-gray-900">
                    {plan.price.toLocaleString('ru-RU')} ₽
                  </span>
                  <span className="text-gray-500 text-sm ml-1">
                    / {plan.duration_days} дней
                  </span>
                </div>

                <p className="text-sm text-gray-600 mb-4">{plan.description}</p>

                <ul className="space-y-2 mb-6 flex-1">
                  {(plan.features || []).map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-gray-700">
                      <CheckCircle size={16} className="text-green-500 flex-shrink-0 mt-0.5" />
                      {feature}
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => handlePay(plan.id)}
                  disabled={!!payingPlanId}
                  className={`w-full py-3 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 disabled:opacity-50 ${
                    isPopular
                      ? 'bg-blue-600 text-white hover:bg-blue-700'
                      : 'bg-gray-900 text-white hover:bg-gray-800'
                  }`}
                >
                  {isPaying ? (
                    <>
                      <Loader className="animate-spin" size={18} />
                      Создание платежа...
                    </>
                  ) : (
                    <>
                      <CreditCard size={18} />
                      {subscription ? 'Продлить' : 'Оплатить'}
                    </>
                  )}
                </button>
              </div>
            )
          })}
        </div>

        {/* История платежей */}
        {payments.length > 0 && (
          <Card>
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <CreditCard size={20} className="text-blue-600" />
              История платежей
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="text-left py-3 px-3 font-semibold">Дата</th>
                    <th className="text-left py-3 px-3 font-semibold">Тариф</th>
                    <th className="text-right py-3 px-3 font-semibold">Сумма</th>
                    <th className="text-center py-3 px-3 font-semibold">Статус</th>
                    <th className="text-right py-3 px-3 font-semibold"></th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map((payment) => {
                    const statusInfo = STATUS_LABELS[payment.status] || STATUS_LABELS.pending
                    const StatusIcon = statusInfo.icon

                    return (
                      <tr key={payment.id} className="border-b hover:bg-gray-50">
                        <td className="py-3 px-3 text-gray-600">
                          {new Date(payment.created_at).toLocaleString('ru-RU')}
                        </td>
                        <td className="py-3 px-3 font-medium">{payment.plan_name}</td>
                        <td className="text-right py-3 px-3 font-semibold">
                          {payment.amount.toLocaleString('ru-RU')} ₽
                        </td>
                        <td className="text-center py-3 px-3">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full ${statusInfo.color}`}>
                            <StatusIcon size={12} />
                            {statusInfo.label}
                          </span>
                        </td>
                        <td className="text-right py-3 px-3">
                          {payment.status === 'pending' && payment.confirmation_url && (
                            <a
                              href={payment.confirmation_url}
                              className="text-blue-600 hover:underline text-xs font-medium"
                            >
                              Оплатить →
                            </a>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </div>
    </Layout>
  )
}

export default PricingPage
